/**
 * The page shell the Integrations page never had (#3480).
 *
 * ## What was wrong
 *
 * `/integrations` rendered three unrelated concepts as flat siblings —
 * connected e-mail mailboxes, portal imports (#1936), and Sync-Agent device
 * pairing (#3071) — with nothing holding them together. The page title, the
 * primary action and the empty state all belonged to the *mailbox* template by
 * accident of composition, so they silently spoke on behalf of the other two.
 *
 * That produced a page which contradicted itself on screen: a full
 * "Noch keine Integrationen" empty state with a primary CTA, directly above a
 * successfully processed import from an integration. No amount of flex fixing
 * removes that — it is a structure problem.
 *
 * ## What this is
 *
 * A real shell that owns the page-level chrome — the `<h1>`, the description and
 * the primary action — so that every section inside it can only ever speak for
 * itself. An empty state below is a *section's* empty state by construction; it
 * has no way to claim the page.
 *
 * Sections are passed as children rather than as props so the page decides its
 * own order and can render a section conditionally without this component
 * growing a flag per feature.
 */

import React from 'react';

import './IntegrationsPageShell.css';

export interface IntegrationsPageShellProps {
  /** Page heading. The ONLY `<h1>` on the page — sections use `<h2>`. */
  title: string;
  /** One line under the heading, describing the page as a whole. */
  description?: string;
  /**
   * The page-level primary action. Optional: a page whose sections each own
   * their own action should pass nothing rather than an inert button.
   */
  action?: React.ReactNode;
  /** The sections, in render order. */
  children: React.ReactNode;
  className?: string;
}

export const IntegrationsPageShell: React.FC<IntegrationsPageShellProps> = ({
  title,
  description,
  action,
  children,
  className = '',
}) => (
  <div className={`integrations-shell ${className}`.trim()}>
    <div className="integrations-shell__container">
      <header className="integrations-shell__header">
        <div className="integrations-shell__heading">
          <h1 className="integrations-shell__title">{title}</h1>
          {description && (
            <p className="integrations-shell__description">{description}</p>
          )}
        </div>
        {action && <div className="integrations-shell__action">{action}</div>}
      </header>

      <div className="integrations-shell__sections">{children}</div>
    </div>
  </div>
);

export default IntegrationsPageShell;
